import { useState } from "react";
import { DateTimeUtils } from "dyl-components";
import { useSelector } from "react-redux";

export const useRangeCalendar = (
    params,
    useQueryParameters = true,
    keys = { start: "start", end: "end" }
) => {
    const { start: startKey, end: endKey } = keys;

    const [timestamp, setTimestamp] = useState(
        useQueryParameters
            ? getTimestampFilter(params.get(startKey), params.get(endKey))
            : "All Time"
    );

    const [isCustomDateRangeOpen, setCustomDateRangeOpen] = useState(false);
    const [isTimeRangeOpen, setTimeRangeOpen] = useState(false);
    const userAuthTimezone = useSelector((state) => state.auth.timezone);

    const [dateRange, setDateRange] = useState(
        useQueryParameters ? getDateRange(params, startKey, endKey) : " - "
    );

    return {
        timestamp,
        setTimestamp,
        isCustomDateRangeOpen,
        setCustomDateRangeOpen,
        isTimeRangeOpen,
        setTimeRangeOpen,
        userAuthTimezone,
        dateRange,
        setDateRange,
        keys,
    };
};

const getDateRange = (params, startKey = "start", endKey = "end") => {
    return `${params.get(startKey)
        ? DateTimeUtils.formatEpoch(params.get(startKey), DateTimeUtils.DATE_FORMAT)
        : ""
    } - ${params.get(endKey)
        ? DateTimeUtils.formatEpoch(params.get(endKey), DateTimeUtils.DATE_FORMAT)
        : ""
    }`;
};

const dateFilter = ({ start, end, params, navigate, url, keys = { start: "start", end: "end" } }) => {
    const { start: startKey, end: endKey } = keys;
    const query = new URLSearchParams(params);
    query.set("page", 1);

    if (start) query.set(startKey, start);
    else query.delete(startKey);

    if (end) query.set(endKey, end);
    else query.delete(endKey);

    const query_string = query.toString();
    navigate(`${url}${query_string ? `?${query_string}` : ""}`);
};

const baseGetData = (setCustomDateRangeOpen, dateRange) => {
    setCustomDateRangeOpen(false);
    const [startString, endString] = dateRange.split(" - ").map((s) => s.trim());
    return {
        start: DateTimeUtils.getUnixTime(startString, DateTimeUtils.DATE_FORMAT),
        end: DateTimeUtils.getUnixTime(`${endString} 11:59 pm`, DateTimeUtils.DATETIME_FORMAT),
    };
};

export const getData = ({
    setCustomDateRangeOpen,
    dateRange,
    params,
    navigate,
    url,
    keys = { start: "start", end: "end" },
}) => {
    dateFilter({
        ...baseGetData(setCustomDateRangeOpen, dateRange),
        params,
        navigate,
        url,
        keys,
    });
};

export const getDataNonQuery = (setCustomDateRangeOpen, dateRange) => {
    return baseGetData(setCustomDateRangeOpen, dateRange);
};

const baseToggleTimeUnit = (timestamp, setTimestamp, returnEmptyObject = false) => {
    setTimestamp(timestamp);

    const TIMESTAMP = {
        Today: "today",
        "This Week": "this_week",
        "This Month": "this_month",
        "All Time": "any",
    };

    if (timestamp === "All Time") {
        return !returnEmptyObject ? { start: false, end: false } : {};
    }
    if (timestamp === "Today") {
        return getCurrentDayTimestamps();
    }
    if (timestamp !== "Custom Range" && timestamp !== "All Time" && timestamp !== "Today") {
        const { start, end } = DateTimeUtils.getDateRange(TIMESTAMP[timestamp]);
        const format = DateTimeUtils.DATE_FORMAT;
        return {
            start: DateTimeUtils.getUnixTime(start, format),
            end: DateTimeUtils.getUnixTime(end, format),
        };
    }
};

export const onToggleTimeUnit = (
    timestamp,
    setTimestamp,
    params,
    navigate,
    url,
    keys = { start: "start", end: "end" }
) => {
    // If selecting Custom Range, we only want to open the custom date range modal
    // without modifying the URL query parameters.
    if (timestamp === "Custom Range") {
        // Just update the timestamp state; dateFilter will be handled by the modal actions.
        setTimestamp(timestamp);
        return;
    }

    dateFilter({
        ...baseToggleTimeUnit(timestamp, setTimestamp),
        params,
        navigate,
        url,
        keys,
    });
};

export const onToggleTimeUnitNonQuery = (timestamp, setTimestamp) => {
    return baseToggleTimeUnit(timestamp, setTimestamp, true);
};

const getCurrentDayTimestamps = (includeSeconds = false) => {
    const { start, end } = DateTimeUtils.getDateRange("today");
    const format = includeSeconds ? "MM-DD-YYYY HH:mm:ss" : "MM-DD-YYYY HH:mm";

    const startTime = `${start} ${includeSeconds ? "00:00:00" : "00:00"}`;
    const endTime = `${end} ${includeSeconds ? "23:59:59" : "23:59"}`;

    return {
        start: DateTimeUtils.getUnixTime(startTime, format),
        end: DateTimeUtils.getUnixTime(endTime, format),
    };
};

export const getTimestampFilter = (start, end, includeSeconds = false) => {
    const { start: currentStart, end: currentEnd } = getCurrentDayTimestamps(includeSeconds);
    return start || end
        ? Number(start) === currentStart && Number(end) === currentEnd
            ? "Today"
            : "Custom Range"
        : "All Time";
};
